<template>
  <div class="dialog-container" v-bind:class="{ 'dialog-full-screen': fullScreen }">
    <transition name="dialog-backdrop-transition" :duration="5000">
      <div v-show="isMounted" class="dialog-backdrop" @click="dismiss">
        <div v-if="mandatoryAuthHeader" class="dialog-close-btn-message">
          <div class="dialog-auth-header-text-container">
            <h4 class="H2 white-color">To continue this action you must be a member of CrowdBlue.</h4>
            <h4 class="H2 white-color">Please sign up or Log in</h4>
          </div>
          <span @click="handleCloseButton" class="dialog-close-btn-container"><i class="fal fa-times fa-2x neutral3-color"></i></span>
        </div>
      </div>
    </transition>

    <transition name="dialog-transition">
      <div v-show="isMounted" class="dialog-center-finder">
        <span class="dialog-focus-trap" tabindex="0" @focus="focusLast"></span>

        <div
          ref="outside"
          :class="{ 'dialog-outside': true, 'dialog-without-scrolling': withoutScrolling, 'dialog-nested': isNested }"
          tabindex="0"
          role="dialog"
          @keyup.esc="dismiss"
        >
          <div class="dialog-inside">
            <slot></slot>
          </div>
        </div>

        <span class="dialog-focus-trap" tabindex="0" @focus="focusFirst"></span>
      </div>
    </transition>
  </div>
</template>

<script>
const FOCUSABLES_SELECTOR = ['a[href]', 'area', 'button', 'input', 'select', 'textarea', '[tabindex]']
  .map(selector => `${selector}:not([tabindex="-1"])`)
  .join(',');

export default {
  data() {
    return {
      isMounted: false,
      activeElementAtCreation: null,
    };
  },
  props: {
    fullScreen: { type: Boolean, default: false },
    withoutScrolling: { type: Boolean, default: false },
    mandatoryAuthHeader: { type: Boolean, default: false },
    handleCloseButton: { type: Function, default: function () {} },
    isNested: { type: Boolean, default: false },
  },
  methods: {
    incrementOpenDialogCount() {
      const originalRootWidth = document.documentElement.offsetWidth;

      const dialogsThatWereOpen = parseFloat(document.documentElement.dataset.dialogsOpen) || 0;

      document.documentElement.dataset.dialogsOpen = dialogsThatWereOpen + 1;

      if (dialogsThatWereOpen === 0) {
        const scrollbarWidth = document.documentElement.offsetWidth - originalRootWidth;
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);

        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    },

    decrementOpenDialogCount() {
      const dialogsOpen = parseFloat(document.documentElement.dataset.dialogsOpen);

      if (dialogsOpen === 1) {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        delete document.documentElement.dataset.dialogsOpen;
      } else {
        document.documentElement.dataset.dialogsOpen = dialogsOpen - 1;
      }
    },

    dismiss() {
      this.$emit('dismiss');
    },

    focusLast() {
      const focusables = this.$refs.outside.querySelectorAll(FOCUSABLES_SELECTOR);
      focusables[focusables.length - 1].focus();
    },

    focusFirst() {
      this.$refs.outside.focus();
    },
  },

  created() {
    if (typeof document !== 'undefined') {
      this.activeElementAtCreation = document.activeElement;
    }
  },

  async mounted() {
    this.isMounted = true;
    await this.$nextTick();

    this.incrementOpenDialogCount();

    const descendantWithAutofocus = this.$refs.outside.querySelector('[autofocus]');
    const elementToFocusOnMount = descendantWithAutofocus || this.$refs.outside;
    elementToFocusOnMount.focus();
  },

  beforeDestroy() {
    this.isMounted = false;

    this.decrementOpenDialogCount();

    if (this.activeElementAtCreation) {
      this.activeElementAtCreation.focus();
    }
  },
};
</script>

<style scoped>
:root[data-dialogs-open] {
  overflow: hidden !important;
  padding-right: var(--scrollbar-width, 0);
}
body[data-dialogs-open] {
  overflow: hidden !important;
  padding-right: var(--scrollbar-width, 0);
}
</style>

<style scoped>
.dialog-container {
  color: black;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  height: 100vh;
  height: 100dvh;
  width: 100vw;
  transform: none !important;
  margin: 0 !important;
  padding: 0 !important;
}

.dialog-backdrop-transition-enter-active {
  transition: opacity 0.2s;
}

.dialog-backdrop-transition-enter,
.dialog-backdrop-transition-leave-to {
  opacity: 0;
}

.dialog-backdrop-transition-enter-to,
.dialog-backdrop-transition-leave {
  opacity: 1;
}

.dialog-backdrop {
  background: rgba(0, 0, 0, 0.7);
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
}

.dialog-center-finder {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
}

.dialog-outside {
  background: var(--white);
  outline: none;
  display: inline-block;
  max-height: 90vh;
  max-height: 90dvh;
  max-width: 90vw;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  pointer-events: initial;
  position: relative;
  border-radius: 20px;
  transform: none;
  margin: auto;
  flex-shrink: 0;
}

.dialog-without-scrolling {
  overflow: hidden;
}

.dialog-close-btn-message {
  text-align: center;
  padding-top: 20px;
}

.dialog-close-btn-container {
  display: flex;
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: var(--white);
  border-radius: 50%;
  width: 48px;
  height: 48px;
  border: 1px solid var(--neutral3);
  justify-content: center;
  align-items: center;
  cursor: pointer;
}

div.dialog-container.dialog-full-screen .dialog-outside {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
}

.dialog-focus-trap {
  position: absolute;
}

.dialog-nested {
  max-height: none;
  overflow: visible;
}

@media screen and (max-width: 1000px) {
  .dialog-auth-header-text-container {
    padding-top: 25px;
  }
  .dialog-close-btn-container {
    width: 40px;
    height: 40px;
    top: 10px;
    right: 10px;
  }
  .dialog-center-finder {
    align-items: center;
    justify-content: center;
  }
}

/* Additional fallback to Safari iPad */
@supports (-webkit-touch-callout: none) {
  .dialog-center-finder {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dialog-outside {
    position: relative;
    transform: none;
    margin: 0;
  }
}
</style>
<style>
.redactor-dropdown,
#redactor-modal-box {
  z-index: 9999 !important;
}
</style>
